import { Dtos, SuretyAccountId } from "@inrev/common";
import {
	useAdminCloseAccount,
	useAdminGenerateAccountReport,
	useAdminReopenAccount,
} from "@portal/api/account/admin";
import { useCloseAccount, useReopenAccount } from "@portal/api/account/agent";
import { UpdateUnderwriting } from "@portal/components/admin/UpdateUnderwriting";
import { HeaderActions } from "@portal/components/layout/HeaderActions";
import { cn } from "@portal/components/lib/utils";
import { InProgressBanner } from "@portal/components/patterns";
import { Text } from "@portal/components/primitives";
import { Tab, TabBar } from "@portal/components/ui/TabBar";
import { useColumnWidth, useColumnWidthSource } from "@portal/hooks/useColumnWidth";
import { useHeaderCollapse } from "@portal/hooks/useHeaderCollapse";
import { useRecentlyViewedEntities } from "@portal/hooks/useRecentlyViewed";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import {
	isVerticalWheel,
	ReportScrolledFromTop,
	ScrolledFromTopReporterContext,
	useScrolledFromTopGestures,
} from "@portal/providers/ScrolledFromTopReporterProvider";
import { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { NonEmptyArray } from "../../shared/types";
import { AdminUserMenu } from "../admin/settings/AdminUserMenu";
import { AdminHeaderActionButtons } from "../admin/shared/AdminHeaderActionButtons";
import { AdminTools } from "../admin/shared/tools/AdminTools";
import { AgentUserMenu } from "../agent/settings/AgentUserMenu";
import { AgentHeaderActionButtons } from "../agent/shared/AgentHeaderActionButtons";
import { AccountHeader, type AccountIdentity } from "../shared/account/AccountHeader";
import { AccountMessages } from "../shared/account/AccountMessages";
import { StickyAccountPanel } from "../shared/account/StickyAccountPanel";
import { AccountTabId, useAccountTabsNavigation } from "./useAccountTabNavigation";

export type AccountTabProps = {
	accountId: SuretyAccountId;
};

type AccountLayoutProps = {
	account: AccountIdentity;
	companies?: (Dtos.Company.Get.Response | Dtos.Admin.SuretyAccount.Get.Response.Company)[];
	tabs: NonEmptyArray<Tab<AccountTabProps> & { id: AccountTabId }>;
};

const ENTITY_VIEW_COLUMN_CLASS = "w-full max-w-[min(950px,calc(100%-96px))] mx-auto";
const ENTITY_VIEW_GUTTER_CLASS = "mx-24";
const EXPANDED_CONTENT_CLASS = "mx-1 md:mx-2 lg:mx-4";

export const AccountLayout: FC<AccountLayoutProps> = ({ account, companies, tabs }) => {
	const [searchParams] = useSearchParams();
	const navigateTabs = useAccountTabsNavigation();
	const { headerCollapsed, setHeaderCollapsed, getHeaderCollapsed } = useHeaderCollapse();
	const tabParamId = searchParams.get("t");
	const subsectionParamId = searchParams.get("st");

	// Derived during render rather than synced through an effect, so the content swap and the
	// scroll reset below land in the same commit. Syncing in an effect painted the outgoing tab
	// at scroll top for a frame before the incoming tab replaced it.
	const selectedTab = useMemo<Tab<AccountTabProps>>(
		() => tabs.find((tab) => tab.id === tabParamId) ?? tabs[0],
		[tabs, tabParamId],
	);

	const scrollRef = useRef<HTMLDivElement | null>(null);
	const { user } = useAuthenticatedUser();
	const { addRecentlyViewedEntity } = useRecentlyViewedEntities();
	const isAdmin = user.type === "admin";
	const columnClass = isAdmin ? ENTITY_VIEW_GUTTER_CLASS : ENTITY_VIEW_COLUMN_CLASS;
	const { generateReport, generateReportIsPending } = useAdminGenerateAccountReport(account.id);

	const handleTabChange = useCallback(
		(tabId: string) => {
			const newTab = tabs.find((tab) => tab.id === tabId);
			if (newTab) {
				navigateTabs({ tabId: newTab.id }, { replace: true });
			}
		},
		[tabs, navigateTabs],
	);

	// Skipped when a subsection is targeted, since that navigation scrolls to the subsection instead.
	useLayoutEffect(() => {
		if (subsectionParamId) return;
		scrollRef.current?.scrollTo({ top: 0, behavior: "instant" });
	}, [selectedTab.id, subsectionParamId]);

	useEffect(() => {
		if (!tabParamId || !subsectionParamId) {
			return;
		}

		const id = subsectionParamId;
		requestAnimationFrame(() => {
			document.getElementById(id)?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		});
	}, [tabParamId, subsectionParamId, selectedTab.id]);

	// When the account is loaded, add it to the recently viewed entities list in the global search context
	useEffect(() => {
		addRecentlyViewedEntity({
			entityType: "account",
			id: account.id,
		});
	}, [account.id, addRecentlyViewedEntity]);

	const { Component: TabContent, expanded, viewport } = selectedTab;
	const contentClass = expanded ? EXPANDED_CONTENT_CLASS : columnClass;

	const handleScrolledFromTopChange = useCallback<ReportScrolledFromTop>(
		(scrolledFromTop) => setHeaderCollapsed(scrolledFromTop),
		[setHeaderCollapsed],
	);

	// On viewport tabs the inner scrollable (e.g. the financials grid) registers itself here
	// so header wheel gestures can be forwarded into it
	const viewportScrollElementRef = useRef<HTMLElement | null>(null);
	const registerScrollElement = useCallback((element: HTMLElement | null) => {
		viewportScrollElementRef.current = element;
	}, []);

	const scrolledFromTopReporting = useMemo(
		() => ({
			reportScrolledFromTop: handleScrolledFromTopChange,
			registerScrollElement,
			getHeaderCollapsed,
		}),
		[handleScrolledFromTopChange, registerScrollElement, getHeaderCollapsed],
	);

	const getActiveScrollElement = useCallback(
		() => (viewport ? viewportScrollElementRef.current : scrollRef.current),
		[viewport],
	);

	const setScrollRef = useCallback((node: HTMLDivElement | null) => {
		scrollRef.current = node;
	}, []);

	// One gesture-state instance drives every wheel-driven collapse/expand/scroll decision for
	// this tab, whether the event lands on the header, the tab bar, or the scroll container —
	// splitting that across independent hook instances per element previously left scrolling
	// stuck once the header collapsed under a stationary cursor.
	const { decideWheel, onScroll: handleContainerScroll } = useScrolledFromTopGestures(
		handleScrolledFromTopChange,
		getActiveScrollElement,
		getHeaderCollapsed,
	);

	// One non-passive listener on the whole chrome+scroll-area wrapper shares that gesture state
	// across every wheel event, only calling preventDefault when overriding the default action.
	// Doing that unconditionally instead is what previously made Chrome stop dispatching wheel
	// events for the gesture until the pointer moved.
	const attachChromeWheel = useCallback(
		(node: HTMLDivElement) => {
			const handler = (e: globalThis.WheelEvent) => {
				if (e.target instanceof Element && e.target.closest("[data-docked-panel-open]") !== null) {
					return;
				}
				const target = getActiveScrollElement();
				if (!target) {
					return;
				}
				// Scoped to the whole tab content wrapper so other scrollable content isn't hijacked into the registered grid.
				const overContent =
					scrollRef.current instanceof Node &&
					e.target instanceof Node &&
					scrollRef.current.contains(e.target);
				if (viewport && overContent) {
					return;
				}

				const decision = decideWheel(target, e);
				if (decision.collapse) {
					e.preventDefault();
					handleScrolledFromTopChange(true);
					return;
				}
				if (decision.block) {
					e.preventDefault();
					return;
				}
				if (decision.expand) {
					handleScrolledFromTopChange(false);
					return;
				}
				// Only forward vertical gestures — horizontal ones drive browser swipe-navigation.
				if (overContent || !isVerticalWheel(e)) {
					return;
				}
				e.preventDefault();
				// deltaMode 1 means the delta is in lines, not pixels (Firefox)
				const deltaPixels = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
				target.scrollBy({ top: deltaPixels });
			};
			node.addEventListener("wheel", handler, { passive: false });
			return () => node.removeEventListener("wheel", handler);
		},
		[viewport, getActiveScrollElement, decideWheel, handleScrolledFromTopChange],
	);

	const column = useColumnWidthSource();
	const tabFit = useColumnWidth(column, (width): "unknown" | "narrow" | "wide" => {
		if (width === undefined) return "unknown";
		return width < 1280 ? "narrow" : "wide";
	});
	const chromeWheelDisposerRef = useRef<(() => void) | undefined>(undefined);
	const setChromeWrapperRef = useCallback(
		(node: HTMLDivElement | null) => {
			chromeWheelDisposerRef.current?.();
			column.ref(node);
			if (!node) {
				chromeWheelDisposerRef.current = undefined;
				return;
			}
			chromeWheelDisposerRef.current = attachChromeWheel(node);
		},
		[attachChromeWheel, column],
	);

	const tabBarClass = cn(
		"pt-4 w-full lg:max-w-[950px] xl:max-w-[1280px] 2xl:max-w-8xl 3xl:max-w-[1600px] gap-2 sm:gap-8",
		isAdmin && tabFit !== "unknown"
			? tabFit === "narrow"
				? "justify-evenly"
				: "justify-between"
			: "justify-evenly lg:justify-between",
	);

	return (
		<div
			data-testid={isAdmin ? "admin-account-layout" : "account-layout"}
			className="w-full h-full flex flex-col"
			data-debug-id="AccountLayout"
		>
			<div className="w-full flex items-center border border-b-bc-subtle px-8 pt-4 pb-5">
				<Text type="caption" color="secondary" truncate className="flex-1 min-w-0">
					{account.displayName}
				</Text>
				<HeaderActions
					userMenu={isAdmin ? <AdminUserMenu /> : <AgentUserMenu />}
					actionButtons={
						isAdmin ? (
							<AdminHeaderActionButtons accountId={account.id} agencyId={account.agencyId} />
						) : (
							<AgentHeaderActionButtons hideActionButtons />
						)
					}
				/>
			</div>
			{isAdmin && <UpdateUnderwriting accountId={account.id} />}
			<div className="flex-1 flex min-h-0">
				<div
					// A full-screen workspace's z-index cannot escape `isolate`, so this element rises above the tools sidebar while one is open.
					className="relative isolate flex-1 flex flex-col min-h-0 min-w-[600px] bg-surface-light [.workspace-full-screen_&]:z-40"
					data-testid="account-layout-page"
					ref={setChromeWrapperRef}
				>
					<div
						data-testid="account-layout-header-wrapper"
						className={cn(
							"shrink-0 grid transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none bg-surface-base",
							headerCollapsed ? "grid-rows-[0fr]" : "grid-rows-[1fr]",
						)}
					>
						{isAdmin && generateReportIsPending && (
							<div className={cn("shrink-0 mt-2", columnClass)}>
								<InProgressBanner testId="generating-inrev-report-banner">
									Generating inRev Report...
								</InProgressBanner>
							</div>
						)}
						<div className="overflow-hidden min-h-0">
							<div className={cn("pt-6 pb-2 flex flex-col space-y-[35px]", columnClass)}>
								<AccountHeader
									account={account}
									companies={companies}
									isAdmin={isAdmin}
									useCloseAccount={isAdmin ? useAdminCloseAccount : useCloseAccount}
									useReopenAccount={isAdmin ? useAdminReopenAccount : useReopenAccount}
									generateReport={generateReport}
									generateReportIsPending={generateReportIsPending}
								/>
							</div>
						</div>
					</div>
					<div className="relative bg-surface-base shrink-0 z-[25] border-b border-b-bc-subtle shadow-md shadow-gray-50">
						<div className={cn(columnClass, "flex")} data-testid="account-layout-tab-bar">
							<TabBar
								options={tabs}
								activeTabId={selectedTab.id}
								onTabChange={handleTabChange}
								className={tabBarClass}
								minimal
								trackingPrefix="account-tab"
							/>
						</div>
						{headerCollapsed && (
							<div
								data-testid="account-layout-collapsed-chrome"
								className="absolute right-2 top-1/2 -translate-y-1/2 hidden xs:block xl:right-8"
							>
								<StickyAccountPanel account={account} isAdmin={isAdmin} column={column} />
							</div>
						)}
					</div>
					<div
						id="account-layout-scroll-container"
						data-testid="account-layout-scroll-container"
						ref={setScrollRef}
						// overscroll-y-contain avoids Chrome dropping wheel events when this container maxes out; y-only since containing x too blocks swipe-navigation.
						className={cn(
							"flex-1 min-h-0 flex flex-col bg-surface-light",
							viewport ? "overflow-hidden" : "overflow-auto overscroll-y-contain",
						)}
						onScroll={viewport ? undefined : handleContainerScroll}
					>
						<div
							className={cn(
								"flex flex-col mb-4",
								contentClass,
								viewport ? "mt-2 flex-1 min-h-0 overflow-hidden" : "mt-6",
							)}
						>
							<div className={cn("flex pb-4", viewport ? "flex-1 min-h-0 flex-col" : "flex-1")}>
								<div className={cn("w-full", viewport && "flex-1 min-h-0 flex flex-col")}>
									{viewport ? (
										<ScrolledFromTopReporterContext.Provider value={scrolledFromTopReporting}>
											<TabContent accountId={account.id} />
										</ScrolledFromTopReporterContext.Provider>
									) : (
										<TabContent accountId={account.id} />
									)}
								</div>
							</div>
						</div>
					</div>
					<AccountMessages accountId={account.id} accountName={account.displayName} />
				</div>
				{isAdmin && account.underwriterUserId !== undefined && (
					<div className="shrink-0">
						<AdminTools
							relation={{ relationType: "account", relationId: account.id }}
							defaultUnderwriterId={account.underwriterUserId}
						/>
					</div>
				)}
			</div>
		</div>
	);
};
